// Lazy-import supabase to avoid pulling 173KB SDK into any chunk that imports fbPixel
const getSupabase = () => import("@/integrations/supabase/client").then(m => m.supabase);

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
    dataLayer: any[];
    __ffGtmActive?: boolean;
    __ffPendingFbq?: any[][];
    __ffFlushPendingFbq?: () => void;
    __ffLastPageViewKey?: string;
    __ffLastPageViewAt?: number;
  }
}

/** Generate a unique event ID for deduplication between client & server */
const generateEventId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/* ─── In-memory dedupe guard ─── */
const recentEvents = new Map<string, number>();
const DEDUPE_WINDOW_MS = 4000;

const buildDedupeKey = (eventName: string, params?: Record<string, any>) => {
  if (!params) return eventName;
  const orderId = params.order_id || params.transaction_id || "";
  const value = params.value ?? "";
  const ids = Array.isArray(params.content_ids)
    ? params.content_ids.join(",")
    : params.content_ids || "";
  const contents = Array.isArray(params.contents)
    ? params.contents.map((c: any) => `${c.id || ""}:${c.quantity || 1}`).join("|")
    : "";
  return `${eventName}::${orderId}::${value}::${ids}::${contents}`;
};

const isDuplicate = (key: string) => {
  const now = Date.now();
  for (const [k, ts] of recentEvents) {
    if (now - ts > DEDUPE_WINDOW_MS) recentEvents.delete(k);
  }
  if (recentEvents.has(key)) return true;
  recentEvents.set(key, now);
  return false;
};

/* ─────────────────────────── GA4 ecommerce schema ─────────────────────────── */

export interface GA4Item {
  item_id: string;
  item_name: string;
  affiliation?: string;
  coupon?: string;
  currency?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price?: number;
  quantity?: number;
}

const FB_TO_GA4_EVENT: Record<string, string> = {
  ViewContent: "view_item",
  AddToCart: "add_to_cart",
  RemoveFromCart: "remove_from_cart",
  ViewCart: "view_cart",
  InitiateCheckout: "begin_checkout",
  AddShippingInfo: "add_shipping_info",
  AddPaymentInfo: "add_payment_info",
  Purchase: "purchase",
  ViewItemList: "view_item_list",
  SelectItem: "select_item",
  Search: "search",
  AddToWishlist: "add_to_wishlist",
  Lead: "generate_lead",
  CompleteRegistration: "sign_up",
};

export const pushGA4Event = (
  eventName: string,
  ecommerce: {
    currency?: string;
    value?: number;
    transaction_id?: string;
    coupon?: string;
    shipping?: number;
    tax?: number;
    payment_type?: string;
    shipping_tier?: string;
    item_list_id?: string;
    item_list_name?: string;
    items?: GA4Item[];
  } = {},
  extras: Record<string, any> = {}
) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: eventName,
    ecommerce: {
      currency: "BDT",
      ...ecommerce,
    },
    ...extras,
  });
};

export const toGA4Item = (
  src: {
    productId?: string | number;
    id?: string | number;
    sku?: string;
    name?: string;
    item_name?: string;
    price?: string | number;
    offer_price?: string | number;
    size?: string;
    color?: string;
    quantity?: number;
    category?: string;
    item_category?: string;
    brand?: string;
    image?: string;
    discount?: number;
  },
  index?: number
): GA4Item => {
  const rawPrice = src.offer_price ?? src.price ?? 0;
  const price =
    typeof rawPrice === "number"
      ? rawPrice
      : Number(String(rawPrice).replace(/[^0-9.]/g, "")) || 0;
  const variant = [src.color, src.size].filter(Boolean).join(" / ") || undefined;
  return {
    item_id: String(src.sku || src.productId || src.id || ""),
    item_name: String(src.name || src.item_name || ""),
    item_brand: src.brand || "BabyBite",
    item_category: src.category || src.item_category || undefined,
    item_variant: variant,
    price,
    quantity: src.quantity ?? 1,
    currency: "BDT",
    ...(src.discount ? { discount: src.discount } : {}),
    ...(typeof index === "number" ? { index } : {}),
  };
};

const waitForFbq = (timeoutMs = 2500): Promise<Window["fbq"] | null> => {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (typeof window.fbq === "function") {
    window.__ffFlushPendingFbq?.();
    return Promise.resolve(window.fbq);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (typeof window.fbq === "function") {
        window.clearInterval(timer);
        window.__ffFlushPendingFbq?.();
        resolve(window.fbq);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(timer);
        resolve(null);
      }
    }, 50);
  });
};

const pushFbq = (args: any[]) => {
  if (typeof window === "undefined") return false;
  if (typeof window.fbq === "function") {
    window.fbq(...args);
    return true;
  }
  window.__ffPendingFbq = window.__ffPendingFbq || [];
  window.__ffPendingFbq.push(args);
  return false;
};

const flushPendingFbq = () => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  const pending = window.__ffPendingFbq?.splice(0) || [];
  pending.forEach((args) => window.fbq(...args));
};

if (typeof window !== "undefined") {
  window.__ffFlushPendingFbq = flushPendingFbq;
}

const ensureFbqReady = (timeoutMs = 2500) => {
  if (typeof window === "undefined") return;
  void waitForFbq(timeoutMs);
};

export const trackPixelEvent = async (
  eventName: string,
  params?: Record<string, any>
) => {
  if (pushFbq(["track", eventName, params])) return;
  const fbq = await waitForFbq();
  if (fbq) fbq("track", eventName, params);
};

const getFbCookies = () => {
  if (typeof document === "undefined") return { fbp: undefined, fbc: undefined };
  const cookies = document.cookie.split("; ").reduce((acc, c) => {
    const [k, v] = c.split("=");
    if (k && v) acc[k] = v;
    return acc;
  }, {} as Record<string, string>);
  return { fbp: cookies._fbp, fbc: cookies._fbc };
};

type TrackEventOptions = {
  skipDedup?: boolean;
  eventId?: string;
  dedupKey?: string;
  waitForCapi?: boolean;
  browserOnly?: boolean;
};

type TrackEventParams = Record<string, any> & {
  ga4?: Parameters<typeof pushGA4Event>[1];
  __options?: TrackEventOptions;
};

export const trackEvent = async (
  eventName: string,
  params?: TrackEventParams,
  userData?: { email?: string; phone?: string }
) => {
  const { ga4: ga4Payload, __options: options, ...fbParams } = params || {};
  const waitForCapi = options?.waitForCapi ?? eventName === "Purchase";

  if (!options?.skipDedup) {
    const dedupeKey = options?.dedupKey || buildDedupeKey(eventName, fbParams);
    if (isDuplicate(dedupeKey)) {
      return;
    }
  }

  const eventId = options?.eventId || generateEventId();

  const ga4EventName = FB_TO_GA4_EVENT[eventName] || eventName.toLowerCase();
  const browserExtras = { eventID: eventId, event_id: eventId };
  pushGA4Event(ga4EventName, ga4Payload || {}, browserExtras);

  const shouldFireBrowserPixel = true;
  const browserParams = { ...fbParams, ...browserExtras };
  const browserPixelPromise = Promise.resolve(
    shouldFireBrowserPixel ? pushFbq(["track", eventName, browserParams, { eventID: eventId }]) : true
  );
  if (shouldFireBrowserPixel && !(await browserPixelPromise)) {
    const fbq = await waitForFbq(eventName === "Purchase" ? 3500 : 2500);
    if (fbq) fbq("track", eventName, browserParams, { eventID: eventId });
  }

  const capiPromise = options?.browserOnly ? Promise.resolve() : (async () => {
    try {
    const supabase = await getSupabase();
    const { fbp, fbc } = getFbCookies();
    await supabase.functions.invoke("fb-conversion-api", {
      body: {
        event_name: eventName,
        event_id: eventId,
        event_source_url: typeof window !== "undefined" ? window.location.href : "",
        user_data: {
          client_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          ...(fbp && { fbp }),
          ...(fbc && { fbc }),
          ...(userData?.email && { em: userData.email }),
          ...(userData?.phone && { ph: userData.phone }),
        },
        custom_data: fbParams,
      },
    });
    } catch {
      // Server-side tracking failure shouldn't affect UX
    }
  })();

  if (waitForCapi) {
    await Promise.allSettled([browserPixelPromise, capiPromise]);
  }
};

/** Track a SPA route change as a Pixel PageView (idempotent guard against rapid re-fires) */
export const trackPageView = (path?: string) => {
  const pagePath = path || (typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "");
  if (typeof window !== "undefined") {
    const now = Date.now();
    if (window.__ffLastPageViewKey === pagePath && now - (window.__ffLastPageViewAt || 0) < 10000) {
      return;
    }
    window.__ffLastPageViewKey = pagePath;
    window.__ffLastPageViewAt = now;
  }
  pushFbq(["track", "PageView", { page_path: pagePath }]);
  ensureFbqReady(2500);
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "page_view", page_path: pagePath });
  }
};
